import { useState } from "react";
import { PREVIEW_LIMIT, visibleClimbTargets } from "@/components/leaderboard/climbTargets";
import RankTargetButton from "@/components/leaderboard/RankTargetButton";

function GhostAvatar({ image, label }) {
  if (image) {
    return <img src={image} alt="" className="h-8 w-8 rounded-full border border-cyan-200 object-cover shadow-[0_0_12px_rgba(34,211,238,0.45)]" />;
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-cyan-200 bg-cyan-400/20 font-display text-[10px] font-black text-cyan-50 shadow-[0_0_12px_rgba(34,211,238,0.45)]">
      {(label || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

export function ClimbOverlay({ markers, activeTargetRank, onHoverTarget, onSelectTarget }) {
  if (!markers || markers.marks.length === 0) return null;
  const tops = markers.marks.map((mark) => mark.top);
  const top = Math.min(...tops, markers.sourceTop);
  const bottom = Math.max(...tops, markers.sourceTop);
  const height = Math.max(bottom - top, 8);

  return (
    <div className="pointer-events-none absolute inset-x-0 z-40" style={{ top, height }} aria-hidden={false}>
      <svg className="absolute left-[4.6rem] h-full w-6" viewBox={`0 0 24 ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <line
          x1="12"
          x2="12"
          y1={markers.sourceTop - top}
          y2={Math.min(...tops) - top}
          stroke="rgb(103 232 249)"
          strokeWidth="1.5"
          strokeDasharray="1 7"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {markers.marks.map((mark) => {
        const active = activeTargetRank === mark.target.target_rank;
        return (
          <div
            key={mark.target.target_rank}
            className="absolute left-[3.35rem] right-0 flex items-center"
            style={{ top: mark.top - top, transform: "translateY(-50%)" }}
          >
            <div className={`flex w-14 flex-col items-center ${active ? "opacity-100" : "opacity-80"}`}>
              <GhostAvatar image={markers.image} label={markers.label} />
              <span className="mt-0.5 max-w-[3.5rem] truncate text-[9px] font-bold lowercase text-cyan-100">{markers.label}</span>
            </div>
            <div className="pointer-events-auto ml-3">
              <RankTargetButton
                target={mark.target}
                active={active || activeTargetRank == null}
                floating
                onHover={onHoverTarget}
                onSelect={onSelectTarget}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClimbPreview({ label, targets, activeTargetRank, onHoverTarget, onSelectTarget, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const visible = visibleClimbTargets(targets, expanded);
  const hiddenCount = Math.max(0, (targets?.length || 0) - PREVIEW_LIMIT);

  return (
    <div
      data-testid="climb-preview"
      role="group"
      aria-label={`Climb targets for ${label}`}
      onClick={(event) => event.stopPropagation()}
      className="mt-2 rounded-xl border border-cyan-300/30 bg-[#080808] p-2"
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-200/80">Boost climb</div>
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-white"
          aria-label={`Close climb preview for ${label}`}
        >
          Close
        </button>
      </div>
      <div className={expanded ? "max-h-64 space-y-1.5 overflow-y-auto pr-1" : "space-y-1.5"}>
        {visible.map((target) => (
          <RankTargetButton
            key={target.target_rank}
            target={target}
            active={activeTargetRank === target.target_rank}
            onHover={onHoverTarget}
            onSelect={onSelectTarget}
          />
        ))}
      </div>
      {hiddenCount > 0 && !expanded && (
        <button
          type="button"
          data-testid="view-all-ranks"
          onClick={() => setExpanded(true)}
          className="mt-2 w-full rounded-lg px-2 py-1.5 text-left text-[11px] font-bold uppercase tracking-wider text-cyan-200 hover:bg-white/[0.04]"
        >
          View all ranks
        </button>
      )}
    </div>
  );
}
